import {
  ActionError,
  BlockTitle,
  Button,
  ConfirmDialog,
  FilterSegments,
  PageHeader,
  PageTitle,
  QrCode,
  Surface,
  cn,
  toast,
  useBreakpoint,
} from '@westhetics/ui'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import { useRegenerateRegistrationLink, useRegistrationLink } from './api'
import { PIECE_FORMATS, type PieceFormat } from './piece-formats'
import { RegistrationPiece } from './registration-piece'

/**
 * Link de cadastro (docs/areas/pacientes.md, "Link de cadastro"; RN-PAC-31, RN-PAC-32; T37).
 * Arquétipo: leitura, com uma utilidade: a peça para imprimir. O link copiável e o QR cabem no
 * celular; a prévia da peça, os formatos e "Imprimir" ficam para o computador. "Gerar outro link" é
 * de gestora e root, com a confirmação dizendo que o anterior deixa de valer na hora.
 */

const PRINT_MARGIN = '10mm'

/** A largura da prévia por formato: a peça escala com o contêiner, então a folha menor aparece menor. */
const PREVIEW_WIDTH: Record<PieceFormat, string> = {
  a4: 'max-w-[520px]',
  a5: 'max-w-[420px]',
  a6: 'max-w-[320px]',
}

/** Copia para a área de transferência; `false` quando o navegador não deixa. */
async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

function LinkSkeleton() {
  return (
    <div
      role="status"
      aria-label="Carregando o link de cadastro"
      className="mt-6 flex flex-col gap-4"
    >
      <div className="bg-alto h-[11px] w-[120px]" />
      <div className="bg-alto h-[14px] w-full max-w-[520px]" />
      <div className="bg-alto h-[168px] w-[168px]" />
    </div>
  )
}

export function RegistrationLinkPage() {
  const isDesk = useBreakpoint('desk')
  const session = useSession()
  const link = useRegistrationLink()
  const regenerate = useRegenerateRegistrationLink()
  const [format, setFormat] = useState<PieceFormat>('a4')
  const [confirming, setConfirming] = useState(false)
  // `link` é uma união discriminada; a variável carrega a forma já carregada ou nada.
  const data = link.isPending || link.isError ? null : link.data
  const page = PIECE_FORMATS.find((f) => f.value === format) ?? PIECE_FORMATS[0]!

  async function copy(url: string) {
    toast(
      (await copyText(url))
        ? 'Link copiado.'
        : 'Não foi possível copiar. Selecione o endereço e copie com o teclado.',
    )
  }

  async function confirmRegenerate() {
    try {
      await regenerate.mutateAsync()
      setConfirming(false)
      toast('Link novo. O anterior deixou de valer.')
    } catch {
      // A recusa fica em `regenerate.error`: a caixa de erro mostra.
    }
  }

  return (
    <>
      <PageHeader
        crumbs={[
          { label: 'Operação' },
          { label: 'Pacientes', href: routes.patients },
          { label: 'Link de cadastro' },
        ]}
        LinkComponent={Link}
        actions={
          isDesk ? (
            <>
              <Button variant="outline" size="sm" asChild>
                <Link to={routes.patients}>Voltar</Link>
              </Button>
              {data ? (
                <Button size="sm" onClick={() => window.print()}>
                  Imprimir
                </Button>
              ) : null}
            </>
          ) : null
        }
      />
      <Page>
        <PageTitle
          title="Link de cadastro"
          subtitle="Mande pelo WhatsApp, cole no Instagram ou imprima para o balcão: a paciente se cadastra sozinha, no celular, antes de chegar."
        />

        {link.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar o link de cadastro"
            error={link.error}
            onRetry={() => void link.refetch()}
          />
        ) : !data ? (
          <LinkSkeleton />
        ) : (
          <>
            <Surface className="max-desk:flex-col desk:items-start desk:justify-between mt-6 flex gap-6">
              <div className="min-w-0 flex-1">
                <div className="eyebrow">O link</div>
                <code className="font-body text-ink mt-3 block text-[13px] leading-[1.6] font-normal break-all">
                  {data.url}
                </code>
                <p className="font-body text-secondary mt-3 max-w-[520px] text-[12px] leading-[1.6] font-light">
                  Vale até você gerar outro. Quem abre informa nome e WhatsApp; e-mail, data de
                  nascimento e CPF são opcionais. O cadastro entra na lista como qualquer outro.
                </p>
                <Button variant="outline" className="mt-5" onClick={() => void copy(data.url)}>
                  Copiar link
                </Button>
              </div>
              <QrCode value={data.url} label="QR do link de cadastro" size={168} />
            </Surface>

            {isDesk ? (
              <section className="mt-10" aria-label="A peça para imprimir">
                <BlockTitle title="A peça para imprimir" />
                <FilterSegments
                  className="mt-4"
                  label="Formato"
                  value={format}
                  onChange={setFormat}
                  options={PIECE_FORMATS.map((f) => ({ value: f.value, label: f.label }))}
                />
                <div className="border-hairline bg-surface mt-4 flex justify-center border p-8">
                  <RegistrationPiece
                    link={data}
                    format={format}
                    className={cn('border-hairline border', PREVIEW_WIDTH[format])}
                  />
                </div>
                <p className="font-body text-secondary mt-3 text-[12px] leading-[1.6] font-light">
                  A prévia é o que sai na impressora, em {page.sheet}, em retrato. "Imprimir" abre a
                  impressão do navegador só com a peça.
                </p>
              </section>
            ) : (
              <p className="font-body text-secondary mt-8 text-[13px] leading-[1.6] font-light">
                Para imprimir a peça com o QR, em cartaz ou cartão, abra esta página no computador.
              </p>
            )}

            {session.canManageClinic ? (
              <section className="mt-10" aria-label="Gerar outro link">
                <BlockTitle title="Gerar outro link" />
                <p className="font-body text-secondary mt-3 max-w-[560px] text-[13px] leading-[1.6] font-light">
                  Se o link vazou ou um cartaz antigo ainda circula, gere outro: o atual deixa de
                  abrir na hora e os cadastros já feitos continuam.
                </p>
                {regenerate.isError ? (
                  <ActionError
                    className="mt-4"
                    title="Não foi possível gerar outro link"
                    description={errorMessage(regenerate.error)}
                  />
                ) : null}
                <Button variant="outline" className="mt-4" onClick={() => setConfirming(true)}>
                  Gerar outro link
                </Button>
              </section>
            ) : null}

            {isDesk
              ? createPortal(
                  <div data-print-root className="hidden print:block">
                    <style>{`@page { size: ${page.page}; margin: ${PRINT_MARGIN}; }`}</style>
                    <RegistrationPiece link={data} format={format} />
                  </div>,
                  document.body,
                )
              : null}
          </>
        )}
      </Page>

      <ConfirmDialog
        open={confirming}
        onOpenChange={(open) => {
          if (!regenerate.isPending) setConfirming(open)
        }}
        title="Gerar outro link de cadastro?"
        effect="O link e o QR atuais deixam de abrir na hora: quem já recebeu precisa do novo. Os cadastros já feitos continuam."
        confirmLabel="Gerar outro link"
        onConfirm={() => void confirmRegenerate()}
        loading={regenerate.isPending}
        loadingLabel="Gerando…"
        tone="primary"
      />
    </>
  )
}
